import * as R from "ramda";
import * as Promise from "bluebird";
import { isPlatform } from "@ionic/react";
import { Device } from "@capacitor/device";
import { StatusBar, Style } from "@capacitor/status-bar";
import { concat, of, from, Observable, timer, EMPTY } from "rxjs";
import {
  mergeMap,
  map,
  catchError,
  filter,
  switchMap,
  debounceTime,
  withLatestFrom,
  finalize,
  takeUntil,
  tap,
  ignoreElements,
  timeout,
} from "rxjs/operators";
import { datasources } from "../db/datasources";
import { UAParser } from "ua-parser-js";
import * as rdd from "react-device-detect";
import DeviceUUID from "../../utils/device-uuid";
import Notify from "../ui/Notify";
import i18n from "../i18n";

import { combineEpics, ofType } from "redux-observable";
import {
  handleLoginFormLoginRequest,
  handleLoginFormLoginResponseError,
  handleLoginFormLoginResponse,
  handleAutoLoginResponse,
  handleAutoLoginResponseError,
  updateProshop,
  handleRegisterFormResponseError,
  handleRegisterForm,
  setDeviceInfo,
  setShowUpdatePasswordModal,
  handleUpdatePasswordError,
  getSubscriptionTypeResponse,
  appStarted,
  getPromoCodeResponse,
  customerPortalOpened,
  types,
  setRegistrationQuote,
  createRegistrationQuote,
  logoUploaded,
  subscribeUserResponse,
  subscriptionPollingStart,
  subscriptionPollingStop,
  subscriptionActivated,
  subscriptionActivationTimedOut,
} from "./userSession.redux";
import {
  setPluginSettings,
  types as pluginTypes,
} from "../plugins/plugin.redux";
import uuid from "uuid/v1";

import {
  apiRequestStart,
  apiRequestEnd,
  apiRequestError,
} from "../api/api.redux";
import { settingsSetAppSettings } from "../settings/settings.redux";
import { getCustomers } from "../bowlers/bowlers.redux";
// import { api } from 'components/api';
import { api, apiConfig } from "../api/api";
import { decryptSession } from "../../utils";
import { initDatasources } from "../db/datasources";
Promise.config({ warnings: false, longStackTraces: false, asyncHooks: false });

const isStripe = apiConfig.payment_gateway === "STRIPE";
const isPaddle = apiConfig.payment_gateway === "PADDLE";

const setStatusBarStyleDark = async () => {
  await StatusBar.setStyle({ style: Style.Dark });
};

const setStatusBarStyleLight = async () => {
  await StatusBar.setStyle({ style: Style.Light });
};

const setWithExpiry = (key, value, ttl) => {
  const now = new Date();

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

const getWithExpiry = (key) => {
  const itemStr = localStorage.getItem(key);

  // if the item doesn't exist, return null
  if (!itemStr) {
    return null;
  }

  const item = JSON.parse(itemStr);
  const now = new Date();

  // compare the expiry time of the item with the current time
  if (now.getTime() > item.expiry) {
    // If the item is expired, delete the item from storage
    // and return null
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
};

const getPromoCode = ({ promoCode, isSandbox }) => {
  // console.log("Get Promo Code");
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("getPromoCode", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "GET",
      params: { promo_code: promoCode, isSandbox },
    })
      .then(success)
      .catch(fail);
  });
};

const getCustomerPortalLinkStripe = ({ customerId, isSandbox }) => {
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("getCustomerPortalLinkStripe", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "GET",
      params: { customerId, isSandbox },
    })
      .then((session) => {
        const url = session.url;
        const popup = window.__customerPortalPopup;
        window.__customerPortalPopup = undefined;
        if (popup && !popup.closed) {
          popup.location.href = url;
        } else {
          // Popup was blocked or never opened — fall back to same-tab navigation.
          window.location.href = url;
        }
        success();
      })
      .catch((error) => {
        const popup = window.__customerPortalPopup;
        window.__customerPortalPopup = undefined;
        if (popup && !popup.closed) popup.close();
        fail(error);
      });
  });
};

const getPatchNotes = () => {
  // console.log("Get Patch notes");
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("wp-json/wp/v2/posts", {
      noWorker: true,
      url: "https://www.spectrebowling.com/",
      type: "GET",
      params: { categories: [3] },
    })
      .then(success)
      .catch(fail);
  });
};

const getWelcomePost = () => {
  // console.log("Get Welcome Post");
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("wp-json/wp/v2/posts", {
      noWorker: true,
      url: "https://www.spectrebowling.com/",
      type: "GET",
      params: { categories: [12] },
    })
      .then(success)
      .catch(fail);
  });
};

const getSubsctiptionType = () => {
  // console.log("Get Subscription Type");
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax(`api/subscriptiontype/paddle`, {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "GET",
      data: {
        isSandbox: apiConfig.isSandbox,
      },
    })
      .then(success)
      .catch(fail);
  });

  // return new Promise((success, fail) => {
  //   ajax("api/subscriptiontype", {
  //     noWorker: true,
  //     url: apiConfig.appUrl,
  //     type: "GET",
  //     data: {},
  //   })
  //     .then(success)
  //     .catch(fail);
  // });
};

const registerUser = (subscription) => {
  // console.log("Register User");
  const { ajax } = api;

  // console.log("Subscription type", subscription.subscriptionType);

  return new Promise((success, fail) => {
    ajax("registerUser", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "POST",
      data: {
        firstname: subscription.firstname,
        lastname: subscription.lastname,
        username: subscription.username,
        password: subscription.password,
        proshopName: subscription.proshopName,
        preferredLang: "en",
      },
    })
      .then((registerResponse) => {
        const { id } = registerResponse;

        if (isStripe) {
          return ajax("subscribeUserStripe", {
            noWorker: true,
            url: apiConfig.appUrl,
            type: "POST",
            data: {
              user: id,
              paymentMethodId: subscription.paymentMethodId,
              priceId: subscription.priceId,
              isSandbox: subscription.isSandbox,
              city: subscription.billingCity,
              country: subscription.billingCountry,
              state: subscription.billingState,
              addressLine1: subscription.addressLine1,
              addressLine2: subscription.addressLine2,
              postal_code: subscription.postal_code,
              type: subscription.subscriptionType,
              promotion_code: subscription.promotion_code,
            },
          });
        } else if (isPaddle) {
          return ajax("subscribeUserPaddle", {
            noWorker: true,
            url: apiConfig.appUrl,
            type: "POST",
            data: {
              user: id,
              customer: subscription.customer,
              priceId: subscription.priceId,
              isSandbox: subscription.isSandbox,
              type: subscription.subscriptionType,
            },
          });
        }
      })
      .then(() => success())
      .catch(fail);
  });
};

const subscribeUser = ({
  subscriptionType,
  customer,
  transactionId,
  user_id,
  isSandbox,
}) => {
  const { ajax } = api;

  return new Promise((success, fail) => {
    setTimeout(() => {
      ajax("subscribeUserPaddle", {
        noWorker: true,
        url: apiConfig.appUrl,
        type: "POST",
        data: {
          user: user_id,
          customer,
          isSandbox,
          transactionId,
          type: subscriptionType,
        },
      })
        .then((response) => success(response))
        .catch(fail);
    }, 100);
  });
};

const getSubscriptionStatus = (userId) => {
  const { ajax } = api;
  return new Promise((success, fail) => {
    ajax("getSubscriptionStatus", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "GET",
      params: { userId },
    })
      .then((response) => success(response))
      .catch(fail);
  });
};

const createQuoteStripe = ({
  country,
  priceId,
  promotion_code,
  addressLine1,
  addressLine2,
  postal_code,
  isSandbox,
}) => {
  // console.log("Create Quote");
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("createQuoteStripe", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "POST",
      data: {
        priceId,
        isSandbox,
        country,
        addressLine1,
        addressLine2,
        postal_code,
        promotion_code,
      },
    })
      .then((response) => {
        window.localStorage.setItem(
          "registrationQuote",
          JSON.stringify(response),
        );
        success(response);
      })
      .catch(fail);
  });
};

const updateQuoteStripe = ({
  quoteId,
  country,
  priceId,
  addressLine1,
  addressLine2,
  postal_code,
  state,
  promotion_code,
  isSandbox,
}) => {
  // console.log("Update Quote");
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("updateQuoteStripe", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "PUT",
      data: {
        quoteId,
        priceId,
        isSandbox,
        country,
        addressLine1,
        addressLine2,
        postal_code,
        state,
        promotion_code,
      },
    })
      .then((response) => {
        window.localStorage.setItem(
          "registrationQuote",
          JSON.stringify(response),
        );
        success(response);
      })
      .catch(fail);
  });
};

const deleteQuoteStripe = ({ quoteId, isSandbox }) => {
  // console.log("Delete Quote");
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("deleteQuoteStripe", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "DELETE",
      data: {
        quoteId,
        isSandbox,
      },
    })
      .then((response) => {
        window.localStorage.setItem("registrationQuote", JSON.stringify({}));
        success(response);
      })
      .catch((fail) => {
        // console.log(fail);
        success();
      });
  });
};

const updatePassword = ({ password, passwordConfirmation }) => {
  // console.log("updating password");
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("user/password", {
      type: "PUT",
      data: {
        password,
        passwordConfirmation,
      },
    })
      .then(() => success())
      .catch(fail);
  });
};

const updateSharedSettings = ({ shared_settings }) => {
  // console.log("updating shared settings");
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("user/shared_settings", {
      type: "PUT",
      data: {
        shared_settings,
      },
    })
      .then(() => success())
      .catch(fail);
  });
};

const updateUser = ({ email, phone }) => {
  // console.log("updating password");
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("user", {
      type: "PUT",
      data: {
        email,
        phone,
      },
    })
      .then(success)
      .catch(fail);
  });
};

const getDeviceFingerprint = () => {
  if (isPlatform("hybrid")) {
    const info = Device.getId();
    return typeof info === "object" && info.identifier
      ? info.identifier
      : typeof info === "string"
        ? info
        : null;
  }
  const deviceUUID = new DeviceUUID();
  return deviceUUID.get();
};

const getDeviceInfoPayload = () => {
  if (isPlatform("hybrid")) {
    const info = Device.getInfo();
    return {
      browser: info.name || "Native App",
      os: `${info.operatingSystem || ""} ${info.osVersion || ""}`.trim(),
      platform: info.platform || "",
    };
  }
  const parsed = UAParser(navigator.userAgent);
  return {
    browser:
      `${parsed.browser.name || ""} ${parsed.browser.version || ""}`.trim(),
    os: `${parsed.os.name || ""} ${parsed.os.version || ""}`.trim(),
    platform: parsed.device.type || "desktop",
  };
};

const login = (login_data) =>
  new Promise((success, fail) => {
    const { ajax } = api;
    const { username, password } = login_data;
    const deviceId = getDeviceFingerprint();
    const deviceInfo = getDeviceInfoPayload();

    ajax(`user/login`, {
      type: "POST",
      data: {
        username,
        password,
        device_id: deviceId,
        device_info: JSON.stringify(deviceInfo),
      },
    })
      .then((access_data) => {
        setWithExpiry(
          "access_token",
          access_data.access_token,
          access_data.expires_in * 1000,
        );
        localStorage.setItem("refresh_token", access_data.refresh_token);
        localStorage.setItem("expires_in", access_data.expires_in);
        if (deviceId) {
          localStorage.setItem("device_id", deviceId);
        }

        const { setAccessToken } = api;
        setAccessToken({
          access_token: access_data,
        });

        success({ access_data });
      })
      .catch((error) => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        // console.log("acces data error->", error);
        fail(error);
      });
  });

const autoLogin = () =>
  new Promise((success, fail) => {
    // console.log("Loging User");
    const { ajax } = api;

    const access_token = getWithExpiry("access_token");
    const refresh_token = localStorage.getItem("refresh_token");
    const expires_in = localStorage.getItem("expires_in") * 1;
    if (!R.isNil(access_token)) {
      const access_data = {
        access_token,
        refresh_token,
        expires_in,
      };
      const { setAccessToken } = api;
      setAccessToken({
        access_token: access_data,
      });
      success({ access_data });
    } else {
      const refresh_token = localStorage.getItem("refresh_token");
      if (!R.isNil(refresh_token)) {
        ajax(`user/refreshToken/${refresh_token}`, {
          type: "GET",
          data: {},
        })
          .then((access_data) => {
            setWithExpiry(
              "access_token",
              access_data.access_token,
              access_data.expires_in * 1000,
            );
            localStorage.setItem(
              "refresh_token",
              JSON.stringify(access_data.refresh_token),
            );
            success({ access_data });
          })
          .catch((error) => {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            // console.log("acces data error->", error);
            fail(error);
          });
      } else {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        fail("refresh_token invalid");
      }
    }
  });

const getUser = ({ access_data }) =>
  new Promise((success, fail) => {
    // console.log("Get logged User");
    const { ajaxSession, setUserSessionUserData } = api;

    ajaxSession(`user`, { type: "GET" })
      .then((user_data) => {
        setUserSessionUserData(user_data);
        success({ access_data, user_data });
      })
      .catch(fail);
  });

const deviceInfoEpic = (action$) =>
  action$.pipe(
    ofType(types.getDeviceInfo),
    mergeMap(() =>
      from(
        new Promise(async (success, reject) => {
          setTimeout(async () => {
            if (isPlatform("hybrid")) {
              document.querySelector("body").classList.add("ion-app");
              await setStatusBarStyleDark();
              const deviceId = Device.getId();
              const deviceInfo = {
                ...Device.getInfo(),
                isWindows: isPlatform("electron"),
                isTablet: isPlatform("tablet"),
                isPhone: isPlatform("iphone"),
                isiPad: isPlatform("ipad"),
                isAndroidTablet: isPlatform("tablet") && isPlatform("android"),
                isMobile: isPlatform("mobile"),
              };

              // console.log("deviceInfo", deviceInfo);

              const wd = window.screen.availWidth;
              const hg = window.screen.availHeight;

              if (deviceInfo.isWindows) {
                document.querySelector("body").classList.add("isWindows");
              }

              let isTabletDevice =
                deviceInfo.isTablet ||
                deviceInfo.isiPad ||
                deviceInfo.isAndroidTablet;
              // alert(`isTableDevice => ${isTabletDevice}`);
              let isMobileDevice = deviceInfo.isMobile && !isTabletDevice;
              let isPortrait = false;

              if (isMobileDevice || isTabletDevice) {
                const orientation = !R.isNil(window.screen.orientation)
                  ? typeof window.screen.orientation === "object"
                    ? window.screen.orientation.type
                      ? window.screen.orientation.type
                          .toLowerCase()
                          .indexOf("landscape") >= 0
                        ? 90
                        : 0
                      : window.screen.orientation.angle
                        ? window.screen.orientation.angle
                        : 0
                    : 0
                  : typeof window.orientation === "object"
                    ? window.orientation.angle
                    : !R.isNil(window.orientation)
                      ? window.orientation
                      : wd <= hg
                        ? 0
                        : 90;

                // alert(`orientation ${orientation}`);
                isPortrait =
                  orientation === 0 || Math.abs(orientation) === 180
                    ? true
                    : false;
                const longestSide = Math.max(wd, hg);
                if (isTabletDevice && longestSide < 1024) {
                  isMobileDevice = true;
                }
              } else {
                if (wd <= hg) {
                  isPortrait = true;
                } else {
                  isPortrait = false;
                }
              }

              if (isMobileDevice || isTabletDevice) {
                document.addEventListener("focusout", (e) => {
                  window.scrollTo(0, 0);
                });
              }

              const checkVersion = () => {
                const agent = window.navigator.userAgent;
                const start = agent.indexOf("OS ");
                if (
                  (agent.indexOf("iPhone") > -1 ||
                    agent.indexOf("iPad") > -1) &&
                  start > -1
                ) {
                  const vStart = agent.substring(start).indexOf(" ") + 1;
                  const vEnd = agent
                    .substring(start)
                    .substring(vStart)
                    .indexOf(" ");
                  return window.Number(
                    agent
                      .substring(start + vStart, start + vStart + vEnd)
                      .replace("_", "."),
                  );
                }
                return 0;
              };

              deviceInfo.iPhoneVersion = checkVersion();

              const isBlockIphone =
                deviceInfo.isiPhone && deviceInfo.iPhoneVersion * 1 < 13;

              const patchNotes = await getPatchNotes();
              const welcomePost = await getWelcomePost();
              const versionNumber =
                window.localStorage.getItem("versionNumber");
              const welcomeShown =
                window.localStorage.getItem("welcomeShown") || false;

              success({
                deviceInfo: {
                  ...deviceInfo,
                  uuid: deviceId,
                  isPortrait,
                  isBlockIphone,
                  isMobileDevice,
                  isTabletDevice,
                },
                patchNotes,
                versionNumber,
                welcomeShown,
                welcomePost: welcomePost[0],
              });
            } else {
              console.log("Device UUID", DeviceUUID);
              const deviceUUID = new DeviceUUID();
              const deviceId = deviceUUID.get();
              const deviceInfoUUID = deviceUUID.parse();
              const deviceSelectors = rdd.getSelectorsByUserAgent(
                navigator.userAgent,
              );
              const deviceInfo = {
                ...deviceInfoUUID,
                ...deviceSelectors,
                isiPad: deviceSelectors.isIPad13,
                // isPhone:
                //   deviceInfo.isIPhone ||
                //   (deviceInfo.isAndroid && deviceInfo.isMobile),
              };

              // console.log("deviceInfo", deviceInfo);
              // alert(`deviceInfo ${JSON.stringify(deviceInfo)}`);

              const wd = window.screen.availWidth;
              const hg = window.screen.availHeight;

              if (deviceInfo.isWindows) {
                document.querySelector("body").classList.add("isWindows");
              }

              let isTabletDevice =
                deviceInfo.isTablet ||
                deviceInfo.isiPad ||
                deviceInfo.isAndroidTablet;

              let isMobileDevice = deviceInfo.isMobile && !isTabletDevice;
              let isPortrait = false;

              if (isMobileDevice || isTabletDevice) {
                const orientation = !R.isNil(window.screen.orientation)
                  ? typeof window.screen.orientation === "object"
                    ? window.screen.orientation.type
                      ? window.screen.orientation.type
                          .toLowerCase()
                          .indexOf("landscape") >= 0
                        ? 90
                        : 0
                      : window.screen.orientation.angle
                        ? window.screen.orientation.angle
                        : 0
                    : 0
                  : typeof window.orientation === "object"
                    ? window.orientation.angle
                    : !R.isNil(window.orientation)
                      ? window.orientation
                      : wd <= hg
                        ? 0
                        : 90;

                // alert(`orientation ${orientation}`);
                isPortrait =
                  orientation === 0 || Math.abs(orientation) === 180
                    ? true
                    : false;
                //              alert(`orientation ${orientation} - isPortrait ${isPortrait}`);
                // alert(`isPortrait ${isPortrait} hg ${hg} wd ${wd}`);
                const longestSide = Math.max(wd, hg);
                if (isTabletDevice && longestSide < 1024) {
                  isMobileDevice = true;
                }
              } else {
                if (wd <= hg) {
                  isPortrait = true;
                } else {
                  isPortrait = false;
                }
              }

              // alert("isPortrait->" + isPortrait);
              if (isMobileDevice || isTabletDevice) {
                document.addEventListener("focusout", (e) => {
                  window.scrollTo(0, 0);
                });
              }

              const checkVersion = () => {
                const agent = window.navigator.userAgent;
                const start = agent.indexOf("OS ");
                if (
                  (agent.indexOf("iPhone") > -1 ||
                    agent.indexOf("iPad") > -1) &&
                  start > -1
                ) {
                  const vStart = agent.substring(start).indexOf(" ") + 1;
                  const vEnd = agent
                    .substring(start)
                    .substring(vStart)
                    .indexOf(" ");
                  return window.Number(
                    agent
                      .substring(start + vStart, start + vStart + vEnd)
                      .replace("_", "."),
                  );
                }
                return 0;
              };

              deviceInfo.iPhoneVersion = checkVersion();

              const isBlockIphone =
                deviceInfo.isiPhone && deviceInfo.iPhoneVersion * 1 < 13;

              const patchNotes = await getPatchNotes();
              const welcomePost = await getWelcomePost();
              const versionNumber =
                window.localStorage.getItem("versionNumber");
              const welcomeShown =
                window.localStorage.getItem("welcomeShown") || false;

              success({
                deviceInfo: {
                  ...deviceInfo,
                  uuid: deviceId,
                  isPortrait,
                  isBlockIphone,
                  isMobileDevice,
                  isTabletDevice,
                },
                patchNotes,
                versionNumber,
                welcomeShown,
                welcomePost: welcomePost[0],
              });
            }
          }, 100);
        }),
      ).pipe(
        map(
          ({
            deviceInfo,
            patchNotes,
            versionNumber,
            welcomeShown,
            welcomePost,
          }) => {
            return setDeviceInfo({
              deviceInfo,
              patchNotes,
              versionNumber,
              welcomeShown,
              welcomePost,
            });
          },
        ),
      ),
    ),
  );

const profileHandleFormEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.profileHandleForm),
    debounceTime(500),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { proshop, app_user },
        },
        state,
      ]) =>
        from(
          new Promise(async (success, reject) => {
            const { proshopDS } = datasources;
            try {
              if (app_user.id) {
                const updatedAppUser = await updateUser({ ...app_user });
                if (proshop.id) {
                  const updatedProshop = await proshopDS.update({
                    ...proshop,
                  });

                  success({
                    proshop: updatedProshop,
                    app_user: updatedAppUser,
                  });
                } else {
                  throw new Error("no proshop");
                }
              } else {
                throw new Error("no user");
              }
            } catch (error) {
              console.warn(error);
              reject(error);
            }
          }),
        ).pipe(
          map(({ proshop }) => {
            return updateProshop({ proshop, app_user });
          }),
          // Without this, one failed save kills the epic and the profile
          // form stops saving for the rest of the session with no sign of
          // why.
          catchError((error) => {
            console.warn("profile save failed", error);
            try {
              Notify.error(i18n.t("form.label.save_failed"));
            } catch (notifyError) {
              // ignore Notify failures
            }
            return EMPTY;
          }),
        ),
    ),
  );

const handleLoginFormLoginRequestEpic = (action$) =>
  action$.pipe(
    ofType(types.handleLoginFormLoginRequest),
    mergeMap(({ payload }) =>
      Observable.create((syncActionsObserver) => {
        const { username, rememberMe } = payload;
        const { setAccessToken } = api;
        setAccessToken({});
        // console.log("calling login");

        return from(
          R.pipeP(login, getUser, (userInfo) => {
            const syncActions = {
              requestStart: () => syncActionsObserver.next(apiRequestStart()),
              requestEnd: () => syncActionsObserver.next(apiRequestEnd()),
              requestError: (error) =>
                syncActionsObserver.next(apiRequestError(error)),
            };

            return initDatasources(userInfo.user_data.id, syncActions)
              .then(() => {
                console.info("Datasources initalized.");
                const { user_data, access_data, login_data } = userInfo;

                const subscriptionInfo =
                  user_data.subscription && user_data.subscription !== ""
                    ? JSON.parse(
                        user_data.subscription.subscription_info || "{}",
                      )
                    : {};

                const subscriptionStatus = subscriptionInfo.status || "NO_INFO";

                syncActionsObserver.next(
                  handleAutoLoginResponse({
                    username,
                    rememberMe,
                    userData: user_data,
                    accessData: access_data,
                    loginData: login_data,
                    deviceData: {},
                    needsSync: false,
                    logged: true,
                    error: "",
                    subscriptionInfo,
                    subscriptionStatus,
                  }),
                );

                const sharedSettings =
                  JSON.parse(user_data.shared_settings || "") || {};

                syncActionsObserver.next(
                  setPluginSettings({
                    plugin_settings: sharedSettings.plugin_settings || {
                      job_board_default_state: "pending",
                      job_board_include_price: true,
                      job_board_currency: "$",
                    },
                    provisionStatus:
                      sharedSettings.spectrepos_provision_status || null,
                    provisionBackend: sharedSettings.spectrepos_backend || null,
                    ownerUsername:
                      sharedSettings.spectrepos_owner_username || null,
                    ownerPassword:
                      sharedSettings.spectrepos_owner_password || null,
                  }),
                );

                syncActionsObserver.next(
                  settingsSetAppSettings({
                    appSettings: sharedSettings.appSettings || {},
                    language: sharedSettings.language || "en",
                  }),
                );

                // const savedSettings = window.localStorage.getItem(
                //   "spectre_user_settings"
                // );
                // const language =
                //   window.localStorage.getItem("spectre_language");
                // syncActionsObserver.next(
                //   settingsSetAppSettings({
                //     appSettings: JSON.parse(savedSettings),
                //     language,
                //   })
                // );
              })
              .catch((error) => {
                syncActionsObserver.next(
                  handleLoginFormLoginResponseError({
                    username: "",
                    userData: {},
                    accessData: {},
                    loginData: {},
                    deviceData: {},
                    needsSync: false,
                    logged: false,
                    error: error.message,
                    subscriptionInfo: null,
                    subscriptionStatus: "NO_INFO",
                  }),
                );
              });
          })(payload).catch((error) => {
            syncActionsObserver.next(
              handleLoginFormLoginResponseError({
                username: "",
                userData: {},
                accessData: {},
                loginData: {},
                deviceData: {},
                needsSync: false,
                logged: false,
                error: "message.error.wrong_password",
                subscriptionInfo: null,
                subscriptionStatus: "NO_INFO",
              }),
            );
          }),
        );
      }),
    ),
  );

const handleAutoLoginEpic = (action$) =>
  action$.pipe(
    ofType(types.handleAutoLogin),
    mergeMap(({}) =>
      Observable.create((syncActionsObserver) => {
        const { setAccessToken } = api;
        setAccessToken({});
        // console.log("calling login");

        return from(
          R.pipeP(autoLogin, getUser, (userInfo) => {
            const syncActions = {
              requestStart: () => syncActionsObserver.next(apiRequestStart()),
              requestEnd: () => syncActionsObserver.next(apiRequestEnd()),
              requestError: (error) =>
                syncActionsObserver.next(apiRequestError(error)),
            };

            return initDatasources(userInfo.user_data.id, syncActions)
              .then(() => {
                console.info("Datasources initalized.");
                const { user_data, access_data, login_data } = userInfo;

                const subscriptionInfo =
                  user_data.subscription && user_data.subscription !== ""
                    ? JSON.parse(
                        user_data.subscription.subscription_info || "{}",
                      )
                    : {};

                const subscriptionStatus = subscriptionInfo.status || "NO_INFO";

                syncActionsObserver.next(
                  handleAutoLoginResponse({
                    userData: user_data,
                    accessData: access_data,
                    loginData: login_data,
                    deviceData: {},
                    needsSync: false,
                    logged: true,
                    error: "",
                    subscriptionInfo,
                    subscriptionStatus,
                  }),
                );

                const sharedSettings =
                  JSON.parse(user_data.shared_settings || "") || {};

                syncActionsObserver.next(
                  setPluginSettings({
                    plugin_settings: sharedSettings.plugin_settings || {
                      job_board_default_state: "pending",
                      job_board_include_price: true,
                      job_board_currency: "$",
                    },
                    provisionStatus:
                      sharedSettings.spectrepos_provision_status || null,
                    provisionBackend: sharedSettings.spectrepos_backend || null,
                    ownerUsername:
                      sharedSettings.spectrepos_owner_username || null,
                    ownerPassword:
                      sharedSettings.spectrepos_owner_password || null,
                  }),
                );

                syncActionsObserver.next(
                  settingsSetAppSettings({
                    appSettings: sharedSettings.appSettings || {},
                    language: sharedSettings.language || "en",
                  }),
                );

                // const savedSettings = window.localStorage.getItem(
                //   "spectre_user_settings"
                // );
                // const language =
                //   window.localStorage.getItem("spectre_language");
                // syncActionsObserver.next(
                //   settingsSetAppSettings({
                //     appSettings: JSON.parse(savedSettings),
                //     language,
                //   })
                // );
              })
              .catch((error) => {
                syncActionsObserver.next(
                  handleAutoLoginResponseError({
                    username: "",
                    userData: {},
                    accessData: {},
                    loginData: {},
                    deviceData: {},
                    needsSync: false,
                    logged: false,
                    error: error,
                    subscriptionInfo: null,
                    subscriptionStatus: "NO_INFO",
                  }),
                );
              });
          })().catch((error) => {
            syncActionsObserver.next(
              handleAutoLoginResponseError({
                username: "",
                userData: {},
                accessData: {},
                loginData: {},
                deviceData: {},
                needsSync: false,
                logged: false,
                error: error,
                subscriptionInfo: null,
                subscriptionStatus: "NO_INFO",
              }),
            );
          }),
        );
      }),
    ),
  );

const handleRegisterFormEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.handleRegisterFormRequest),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return from(registerUser(payload)).pipe(
        map(() => {
          return handleLoginFormLoginRequest({
            username: payload.username,
            password: payload.password,
            rememberMe: true,
          });
        }),
        catchError((error) => {
          const errorResponse = JSON.parse(error.response);
          return of(
            handleRegisterFormResponseError({
              error: `message.error.${errorResponse.code}-${errorResponse.message}`,
            }),
          );
        }),
      );
    }),
  );

// Long enough to outlast Cloudflare (100s) and Apache (120s), so a request
// that is merely slow still gets its real answer.
const SUBSCRIBE_REQUEST_TIMEOUT_MS = 130000;

const subscribeUserEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.subscribeUserRequest),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return from(subscribeUser(payload)).pipe(
        // subscribeUserRequest puts a full-screen spinner up and only the
        // response takes it down, so a call that never settles leaves the
        // user staring at it with no way out. That is reachable: a checkout
        // can complete at Paddle and this call still never reach us.
        timeout(SUBSCRIBE_REQUEST_TIMEOUT_MS),
        mergeMap((response) => {
          // Backend says it could not confirm subscription_id with Paddle yet
          // — open the wait banner and start polling instead of reloading
          // into a broken state.
          if (response && response.pending) {
            return of(
              subscribeUserResponse(),
              subscriptionPollingStart({ userId: payload.user_id }),
            );
          }
          // Normal success: keep the legacy reload so we re-bootstrap with
          // the new subscription data.
          if (typeof window !== "undefined") {
            setTimeout(() => window.location.reload(), 500);
          }
          return of(subscribeUserResponse());
        }),
        catchError((error) => {
          try {
            JSON.parse(error.response);
          } catch (e) {
            // ignore — keeps the original best-effort logging behaviour
          }
          console.warn("subscribe request failed", error);
          try {
            Notify.error(i18n.t("subscriptionActivation.requestFailed"));
          } catch (notifyError) {
            // ignore Notify failures
          }
          return of(subscribeUserResponse());
        }),
      );
    }),
  );

const SUBSCRIPTION_POLL_INTERVAL_MS = 2500;
const SUBSCRIPTION_POLL_TIMEOUT_MS = 60000;

const subscriptionPollingEpic = (action$) =>
  action$.pipe(
    ofType(types.subscriptionPollingStart),
    switchMap(({ payload }) => {
      const userId = payload && payload.userId;
      if (!userId) {
        return of(subscriptionPollingStop());
      }
      const stop$ = action$.pipe(
        ofType(
          types.subscriptionPollingStop,
          types.subscriptionActivated,
          types.handleLogout,
        ),
      );
      const timeout$ = timer(SUBSCRIPTION_POLL_TIMEOUT_MS).pipe(
        map(() => subscriptionActivationTimedOut()),
      );
      const tick$ = timer(0, SUBSCRIPTION_POLL_INTERVAL_MS).pipe(
        mergeMap(() =>
          from(getSubscriptionStatus(userId)).pipe(
            mergeMap((response) => {
              if (
                response &&
                response.pending === false &&
                response.subscriptionId
              ) {
                return of(
                  subscriptionActivated({
                    subscriptionId: response.subscriptionId,
                  }),
                );
              }
              return EMPTY;
            }),
            catchError(() => EMPTY),
          ),
        ),
      );
      return concat(tick$, timeout$).pipe(takeUntil(stop$));
    }),
  );

const subscriptionActivatedEpic = (action$) =>
  action$.pipe(
    ofType(types.subscriptionActivated),
    tap(() => {
      try {
        Notify.success(
          i18n.t(
            "subscriptionActivation.success",
            "Your subscription is now active!",
          ),
        );
      } catch (e) {
        // ignore Notify failures
      }
      if (typeof window !== "undefined") {
        setTimeout(() => window.location.reload(), 1500);
      }
    }),
    ignoreElements(),
  );

const handleUpdatePasswordFormEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.handleUpdatePassword),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return from(updatePassword(payload)).pipe(
        mergeMap(() => {
          return of(setShowUpdatePasswordModal({ show: false }), {
            type: types.handleLogout,
          });
        }),
        catchError((error) => {
          return of(handleUpdatePasswordError({ error }));
        }),
      );
    }),
  );

const handleUpdateSharedSettingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.updateSharedSettings),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return from(updateSharedSettings(payload)).pipe(
        map(() => {
          return setPluginSettings({
            plugin_settings: payload.shared_settings.plugin_settings,
            provisionStatus:
              payload.shared_settings.spectrepos_provision_status || null,
            provisionBackend:
              payload.shared_settings.spectrepos_backend || null,
            ownerUsername:
              payload.shared_settings.spectrepos_owner_username || null,
            ownerPassword:
              payload.shared_settings.spectrepos_owner_password || null,
          });
        }),
        catchError((error) => {
          return of(setPluginSettings({ plugin_settings: {} }));
        }),
      );
    }),
  );

const handleLogoutEpic = (action$) =>
  action$.pipe(
    ofType(types.handleLogout),
    map(() => {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      window.location.reload();
      return of();
    }),
  );

const getSubscriptionTypeEpic = (action$) =>
  action$.pipe(
    ofType(types.getSubscriptionType),
    mergeMap(() =>
      from(getSubsctiptionType()).pipe(
        map((subscriptionType) =>
          // console.log(subscriptionType) ||
          getSubscriptionTypeResponse(subscriptionType),
        ),
        catchError((error) => {
          console.error(error);
          return of(
            getSubscriptionTypeResponse({
              subscriptionType: [],
            }),
          );
        }),
      ),
    ),
  );

const getPromoCodeEpic = (action$) =>
  action$.pipe(
    ofType(types.getPromoCode),
    mergeMap(({ payload }) =>
      from(getPromoCode(payload)).pipe(
        map((promoCode) =>
          // console.log(promoCode) ||
          getPromoCodeResponse({
            promoCode: promoCode.data[0],
            quoteValues: payload.quoteValues,
          }),
        ),
        catchError((error) => {
          console.error(error);
          return of(
            getPromoCodeResponse({
              promoCode: null,
              quoteValues: payload.quoteValues,
            }),
          );
        }),
      ),
    ),
  );

const updatePromoCodeQuoteEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getPromoCodeResponse),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const { quoteValues } = payload;

      return from(
        deleteQuoteStripe({
          quoteId: quoteValues.quoteId,
          isSandbox: quoteValues.isSandbox,
        }),
      ).pipe(
        map(() => {
          const { promoCode, quoteValues } = payload;
          const {
            isSandbox,
            country,
            state,
            priceId,
            addressLine1,
            addressLine2,
            postal_code,
          } = quoteValues;
          const isPromoCodeValid = promoCode
            ? promoCode.id
              ? promoCode.id !== ""
                ? promoCode.active
                : false
              : false
            : false;

          return createRegistrationQuote({
            priceId,
            country,
            state,
            addressLine1,
            addressLine2,
            postal_code,
            promotion_code: isPromoCodeValid ? promoCode.id : undefined,
            isSandbox,
          });
        }),
        catchError((error) => {
          console.error(error);
          return of();
        }),
      );
    }),
  );

const startAppEpic = (action$) =>
  action$.pipe(
    ofType(types.startApp),
    map(() => {
      const host = window.location.host;
      return appStarted({
        isSandbox:
          host.indexOf("localhost") >= 0 ||
          host.indexOf("v3.") >= 0 ||
          host.indexOf("sandbox.") >= 0,
      });
    }),
  );

const handleOpenCustomerPortalEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.handleOpenCustomerPortal),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      return from(
        getCustomerPortalLinkStripe({
          customerId: state.userSession.subscriptionInfo.customer,
          isSandbox: state.userSession.isSandbox,
        }),
      ).pipe(
        map(() => {
          return customerPortalOpened({});
        }),
        catchError((error) => {
          console.warn("Customer portal link error", error);
          Notify.error(
            i18n.t(
              "freeSubscriptionModal.portalError",
              "We couldn't open the billing portal. Please try again or contact support.",
            ),
          );
          return of(customerPortalOpened({}));
        }),
      );
    }),
  );

const createQuoteStripeEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.createRegistrationQuote),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return from(createQuoteStripe(payload)).pipe(
        map((registrationQuote) => {
          return setRegistrationQuote({ registrationQuote });
        }),
        catchError((error) => {
          return of(setRegistrationQuote({ registrationQuote: {} }));
        }),
      );
    }),
  );

const updateQuoteStripeEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.updateRegistrationQuote),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return from(updateQuoteStripe(payload)).pipe(
        map((registrationQuote) => {
          return setRegistrationQuote({ registrationQuote });
        }),
        catchError((error) => {
          return of(setRegistrationQuote({ registrationQuote: {} }));
        }),
      );
    }),
  );

const deleteQuoteStripeEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.deleteRegistrationQuote),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return from(deleteQuoteStripe(payload)).pipe(
        map(() => {
          return setRegistrationQuote({ registrationQuote: {} });
        }),
        catchError((error) => {
          return of(setRegistrationQuote({ registrationQuote: {} }));
        }),
      );
    }),
  );

const getRegistrationQuoteEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getRegistrationQuote),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      const rQuote = JSON.parse(
        window.localStorage.getItem("registrationQuote") || "{}",
      );
      return from(Promise.resolve(rQuote)).pipe(
        map((registrationQuote) => {
          return setRegistrationQuote({ registrationQuote });
        }),
        catchError((error) => {
          return of(setRegistrationQuote({ registrationQuote: {} }));
        }),
      );
    }),
  );

const uploadLogoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.uploadLogo),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { imageBase64, proshop },
        },
        state,
      ]) => {
        const processLogo = () =>
          new Promise((success, fail) => {
            let fileName = `logo_${uuid()}.png`;
            // let fileName = `${
            //   state.userSession.userData.id
            // }/logo_${uuid()}.png`;

            const { getSessionApiUrl, checkOffline, ajaxSession } = api;

            const sendImageToServer = ({ image, imageFileName }) =>
              R.pipeP(
                () => Promise.resolve("images/uploadImageBase64"),
                (url) => {
                  // console.log("url", url);
                  return ajaxSession(url, {
                    type: "POST",
                    noWorker: true,
                    data: {
                      image,
                      imageFileName,
                    },
                  });
                },
              )();

            const saveFileOnServer = (fileProps) =>
              R.pipeP(checkOffline, (offline) =>
                offline ? Promise.resolve() : sendImageToServer(fileProps),
              )();

            saveFileOnServer({ image: imageBase64, imageFileName: fileName })
              .then(async () => {
                const { proshopDS } = datasources;

                await proshopDS.update({
                  ...proshop,
                  logo: fileName,
                });

                success({
                  fileName,
                  proshop: {
                    ...proshop,
                    logo: fileName,
                  },
                });
                // console.log("Image saved success!!");
              })
              .catch(fail);
          });
        return from(processLogo()).pipe(
          map((result) => {
            // console.log("file saved update action");
            return logoUploaded(result);
          }),
        );
      },
    ),
  );

export default combineEpics(
  deviceInfoEpic,
  profileHandleFormEpic,
  handleLoginFormLoginRequestEpic,
  handleAutoLoginEpic,
  handleRegisterFormEpic,
  handleLogoutEpic,
  handleUpdatePasswordFormEpic,
  getSubscriptionTypeEpic,
  startAppEpic,
  getPromoCodeEpic,
  handleOpenCustomerPortalEpic,
  createQuoteStripeEpic,
  updateQuoteStripeEpic,
  getRegistrationQuoteEpic,
  deleteQuoteStripeEpic,
  updatePromoCodeQuoteEpic,
  handleUpdateSharedSettingsEpic,
  uploadLogoEpic,
  subscribeUserEpic,
  subscriptionPollingEpic,
  subscriptionActivatedEpic,
);
